/**
 * ProjectManager - Manages shared projects (API-backed + real-time WebSocket sync).
 *
 * - Projects are stored in MongoDB (via /api/projects)
 * - Each open project subscribes to /ws/projects/{id} for live updates
 * - Superadmins see ALL projects; members see only their team's projects
 * - Optionally lets user pick a team when creating a project
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SharedProject,
  Team,
  listSharedProjects,
  createSharedProject,
  deleteSharedProject,
  listTeams,
} from '../services/api';

// Re-export the "Project" shape the rest of the app still uses
export type Project = {
  id: string;
  name: string;
  description: string;
  contextPath?: string;
  instructions?: string;
  backlog?: string;
  createdAt: string;
  updatedAt: string;
  // team info
  teamId?: string | null;
  teamName?: string;
  owner?: string;
  isShared?: boolean;
};

function sharedToProject(p: SharedProject, teams: Team[]): Project {
  const team = teams.find(t => t.id === p.team_id);
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    contextPath: p.context_path,
    instructions: p.instructions,
    backlog: p.backlog ?? undefined,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    teamId: p.team_id,
    teamName: team?.name,
    owner: p.owner,
    isShared: !!p.team_id,
  };
}

interface ProjectManagerProps {
  currentProject: Project | null;
  onProjectSelect: (project: Project) => void;
  onProjectCreate: (project: Project) => void;
  onProjectUpdate: (project: Project) => void;
  onProjectDelete: (projectId: string) => void;
  isSuperAdmin?: boolean;
}

// Helper to derive WS base URL from the API URL
function getWsBaseUrl(): string {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl) {
    return apiUrl.replace(/^https?/, (m: string) => m === 'https' ? 'wss' : 'ws');
  }
  const hostname = window.location.hostname;
  const isCloud = !hostname.includes('localhost') && !hostname.includes('127.0.0.1');
  if (isCloud) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}`;
  }
  return 'ws://localhost:8000';
}

export const ProjectManager: React.FC<ProjectManagerProps> = ({
  currentProject,
  onProjectSelect,
  onProjectCreate,
  onProjectUpdate,
  onProjectDelete,
  isSuperAdmin = false,
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProjectList, setShowProjectList] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newTeamId, setNewTeamId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);

  // WebSocket ref for current project
  const wsRef = useRef<WebSocket | null>(null);

  // ---- Load data -------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const [projectsRes, teamsRes] = await Promise.all([
        listSharedProjects(),
        listTeams(),
      ]);
      const teamList = teamsRes.teams;
      setTeams(teamList);
      setProjects(projectsRes.projects.map(p => sharedToProject(p, teamList)));
    } catch (e: any) {
      setApiError(e?.response?.data?.detail || e.message || 'Error loading projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---- WebSocket: subscribe to live project updates --------------------

  // Keep teams ref for ws closure
  const teamsRef = useRef<Team[]>([]);
  useEffect(() => { teamsRef.current = teams; }, [teams]);

  useEffect(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (!currentProject?.id) return;

    const url = `${getWsBaseUrl()}/ws/projects/${currentProject.id}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (_) {
      return;
    }
    wsRef.current = ws;

    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'project_updated' && msg.project) {
          const updated = sharedToProject(msg.project, teamsRef.current);
          setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
          onProjectUpdate(updated);
        }
        if (msg.type === 'project_deleted') {
          onProjectDelete(msg.project_id);
        }
      } catch (_) {}
    };

    ws.onerror = () => console.warn('[WS] error for project', currentProject.id);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id]);

  // ---- Handlers --------------------------------------------------------

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const created = await createSharedProject({
        name: newProjectName.trim(),
        description: newProjectDesc.trim(),
        team_id: newTeamId || undefined,
      });
      const project = sharedToProject(created, teams);
      setProjects(prev => [project, ...prev]);
      onProjectCreate(project);
      setNewProjectName('');
      setNewProjectDesc('');
      setNewTeamId('');
      setShowCreateModal(false);
    } catch (e: any) {
      setApiError(e?.response?.data?.detail || e.message || 'Error creating project');
    }
  };

  const handleSelectProject = (project: Project) => {
    onProjectSelect(project);
    setShowProjectList(false);
  };

  const handleDeleteProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este proyecto? Esta acción no se puede deshacer.')) return;
    try {
      await deleteSharedProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      onProjectDelete(projectId);
    } catch (e: any) {
      setApiError(e?.response?.data?.detail || e.message || 'Error deleting project');
    }
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4">
      <h3 className="text-lg font-bold text-gft-dark mb-3 flex items-center gap-2">
        <span>📁</span>
        Proyecto
        {isSuperAdmin && (
          <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">
            superadmin
          </span>
        )}
      </h3>

      {apiError && (
        <div className="mb-2 p-2 bg-red-50 text-red-600 text-xs rounded-lg flex justify-between">
          <span>{apiError}</span>
          <button onClick={() => setApiError(null)}>✕</button>
        </div>
      )}

      {/* Current Project */}
      {currentProject ? (
        <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-lg">📂</span>
                <h4 className="font-semibold text-gft-dark truncate">{currentProject.name}</h4>
                {currentProject.isShared && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full">
                    🔗 {currentProject.teamName || 'Compartido'}
                  </span>
                )}
              </div>
              {currentProject.description && (
                <p className="text-xs text-gray-500 mt-1 truncate">{currentProject.description}</p>
              )}
              <p className="text-[10px] text-gray-400 mt-1">
                Actualizado: {formatDate(currentProject.updatedAt)}
              </p>
            </div>
            <div className="flex gap-1 ml-2">
              <button
                onClick={() => { loadData(); setShowProjectList(true); }}
                className="p-1.5 text-gray-500 hover:text-gft-purple hover:bg-white rounded"
                title="Cambiar proyecto"
              >
                🔄
              </button>
            </div>
          </div>
          {currentProject.backlog && (
            <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
              <span>✅</span>
              <span>Backlog guardado</span>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-3 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-center">
          <span className="text-3xl mb-2 block">📁</span>
          <p className="text-sm text-gray-600 mb-2">No hay proyecto seleccionado</p>
          <p className="text-xs text-gray-400">Crea o selecciona un proyecto para comenzar</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex-1 px-3 py-2 bg-gft-purple text-white rounded-lg hover:bg-purple-700
            transition-colors text-sm font-medium flex items-center justify-center gap-1"
        >
          <span>➕</span>
          <span>Nuevo</span>
        </button>
        <button
          onClick={() => { loadData(); setShowProjectList(true); }}
          className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200
            transition-colors text-sm font-medium flex items-center justify-center gap-1"
        >
          <span>📋</span>
          <span>Proyectos ({loading ? '…' : projects.length})</span>
        </button>
      </div>

      {/* ---- Modal: Create Project ---- */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="text-lg font-bold text-gft-dark flex items-center">
                <span className="mr-2">➕</span>
                Nuevo Proyecto
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Proyecto *
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  placeholder="Ej: Portal de Clientes v2"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gft-purple
                    focus:border-gft-purple"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleCreateProject()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={newProjectDesc}
                  onChange={e => setNewProjectDesc(e.target.value)}
                  placeholder="Breve descripción del proyecto…"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gft-purple
                    focus:border-gft-purple resize-none"
                />
              </div>
              {teams.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipo (compartir con)
                  </label>
                  <select
                    value={newTeamId}
                    onChange={e => setNewTeamId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gft-purple
                      focus:border-gft-purple bg-white"
                  >
                    <option value="">Solo yo (sin equipo)</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewProjectName('');
                  setNewProjectDesc('');
                  setNewTeamId('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim()}
                className="px-4 py-2 bg-gft-purple text-white rounded-lg hover:bg-purple-700
                  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Crear Proyecto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Modal: Project List ---- */}
      {showProjectList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gft-dark flex items-center">
                <span className="mr-2">📋</span>
                Proyectos {isSuperAdmin ? '(todos)' : 'de mi equipo'}
              </h3>
              <button
                onClick={loadData}
                className="p-1.5 text-gray-400 hover:text-gft-purple rounded transition"
                title="Recargar"
              >
                🔄
              </button>
            </div>
            <div className="p-3 border-b">
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="🔍 Buscar proyecto…"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gft-purple
                  focus:border-gft-purple text-sm"
              />
            </div>
            <div className="flex-1 overflow-auto p-3">
              {loading ? (
                <div className="text-center py-8 text-gray-400">Cargando…</div>
              ) : filteredProjects.length > 0 ? (
                <div className="space-y-2">
                  {filteredProjects.map(project => (
                    <div
                      key={project.id}
                      onClick={() => handleSelectProject(project)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all
                        hover:border-gft-purple hover:bg-purple-50 ${
                          currentProject?.id === project.id
                            ? 'border-gft-purple bg-purple-50'
                            : 'border-gray-200'
                        }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>📂</span>
                            <h4 className="font-medium text-gray-800 truncate">{project.name}</h4>
                            {project.backlog && (
                              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                ✓ Backlog
                              </span>
                            )}
                            {project.isShared && (
                              <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">
                                🔗 {project.teamName}
                              </span>
                            )}
                          </div>
                          {project.description && (
                            <p className="text-xs text-gray-500 mt-1 truncate">{project.description}</p>
                          )}
                          {project.owner && (
                            <p className="text-[10px] text-gray-400 mt-0.5">por {project.owner}</p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">
                            {formatDate(project.updatedAt)}
                          </p>
                        </div>
                        <button
                          onClick={e => handleDeleteProject(project.id, e)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                          title="Eliminar proyecto"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl block mb-2">📁</span>
                  {searchTerm ? 'No se encontraron proyectos' : 'No hay proyectos aún'}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-between">
              <button
                onClick={() => { setShowCreateModal(true); setShowProjectList(false); }}
                className="px-4 py-2 text-gft-purple hover:bg-purple-50 rounded-lg transition-colors"
              >
                ➕ Nuevo Proyecto
              </button>
              <button
                onClick={() => { setShowProjectList(false); setSearchTerm(''); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManager;
